import { SpongeTextStyle } from "../interfaces/PhaserOverides";

/**
 * the colour pallete used that should flow through the majority of re-usable
 * components
 *
 * @export
 * @class ColorPalette
 */
export class ColorPalette {
  /**
   * primary colour as number
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly primary: number = 0xef4a39;
  /**
   * secondary colour as number
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly secondary: number = 0xfbb315;

  /**
   * default hover colour
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly hover: number = 0xd13f32;

  /**
   * default down state colour
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly primary_darken: number = 0xaa3329;
  /**
   * default down state colour
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly label: number = 0xffffff;
  /**
   * default disabled colour
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly disabled: number = 0xaaaaaa;
  /**
   * default game / stage background colour
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly background: number = 0xffffff;

  /**
   * preload background colour.
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly preloadBackground: number = 0xfbb315;
  /**
   * preload fill bar colour
   *
   * @type {number}
   * @memberof ColorPalette
   */
  readonly preloadFill: number = 0xef4a39;
}
/**
 * font definitions, currently these used a interim class to help type. but full API can be found at:
 *
 * @export
 * @class Fonts
 */
export class Fonts {
  /**
   * font for preloader
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly preloader: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "40px",
    fontStyle: "900",
    align: "center",
    color: "#ffffff",
  };
  /**
   * debug font style
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly debug: SpongeTextStyle = {
    fontFamily: "Roboto Mono, monospace",
    fontSize: "14px",
    color: "#222222",
  };

  /**
   * main headings
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly h1: SpongeTextStyle = {
    fontFamily: "Roboto Condensed, Arial",
    fontSize: "26px",
    color: "#222222",
  };

  /**
   * default body
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly body: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "26px",
    color: "#FFFFFF",
    fontStyle: "400",
  };

  /**
   * default button
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly button: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "40px",
    fontStyle: "900",
    color: "#ffffff",
    align: "center",
  };
  /**
   * default button
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly passwordStrengthHeading: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "48px",
    fontStyle: "900",
    color: "#ffffff",
    align: "center",
    wordWrap: {
      width: 400,
    },
  };
  /**
   * default button
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly passwordStrengthValue: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "60px",
    fontStyle: "900",
    color: "#ffffff",
    align: "center",
  };

  /**
   * default button
   *
   * @type {SpongeTextStyle}
   * @memberof Fonts
   */
  readonly iconButton: SpongeTextStyle = {
    fontFamily: "Roboto, arial",
    fontSize: "24px",
    color: "#000000",
    align: "center",
  };

  readonly rtlTest: SpongeTextStyle = {
    fontFamily: "Arial",
    fontSize: "24px",
    rtl: true,
    color: "#ffffff",
    // fixedWidth:300,
    wordWrap: {
      width: 300,
    },
  };
  readonly ltrTest: SpongeTextStyle = {
    fontFamily: "Arial",
    fontSize: "24px",
    rtl: false,
    color: "#ffffff",
    // fixedWidth:300,
    wordWrap: {
      width: 300,
    },
  };
  readonly uiLabel: SpongeTextStyle = {
    fontFamily: "Arial, san-serif",
    fontSize: "24px",
    align: "right",
    color: "#000000",
    wordWrap: {
      width: 300,
    },
  };
  readonly question: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "40px",
    fontStyle: "900",
    align: "center",
    color: "#b4e2f5",
    wordWrap: {
      width: 400,
    },
  };
  readonly questionFull: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "24px",
    fontStyle: "900",
    align: "center",
    color: "#b4e2f5",
    wordWrap: {
      width: 400,
    },
  };
  readonly answer: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "36px",
    fontStyle: "900",
    align: "center",
    color: "#b4e2f5",
    wordWrap: {
      width: 400,
    },
  };
  readonly pauseButton: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "36px",
    fontStyle: "900",
    align: "left",
    color: "#ffffff",
    wordWrap: {
      width: 400,
    },
  };

  readonly voButton: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "36px",
    fontStyle: "900",
    align: "left",
    color: "#ffffff",
    wordWrap: {
      width: 400,
    },
  };
  readonly countdown: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "150px",
    fontStyle: "900",
    align: "center",
    color: "#b4e2f5",
  };
  readonly timer: SpongeTextStyle = {
    fontFamily: "Poppins",
    fontSize: "50px",
    fontStyle: "900",
    align: "center",
    color: "#ffffff",
  };
}

/**
 * Scaling model handles any settings to do with the way the game is displayed.
 *
 * @export
 * @class ScalingModel
 */
export class ScalingModel {
  /**
   * Should we force a specific origintation on mobile?
   *
   * @type {boolean}
   * @memberof ScalingModel
   */
  readonly shouldForceOrientationOnMobile: boolean = true;
  /**
   * if origintation is forced, do we desire landscape?
   *
   * @type {boolean}
   * @memberof ScalingModel
   */
  readonly shouldForceLandscaprOnMobile: boolean = true;
  /**
   * this flag will attempt to fullscreen whenever possible
   *
   * @type {boolean}
   * @memberof ScalingModel
   */
  readonly attemptFullscreenWherePossible: boolean = false;
}
